import { marked } from 'marked';

marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Pre-process bare host-like tokens (e.g. `app.dealton.ai`,
 * `gauge.geomet.ai/dashboard`) into explicit markdown links so `marked`'s
 * GFM autolinker — which only catches `http(s)://...` and `www.` — wraps
 * them in `<a>` tags too.
 *
 * Conservative: only auto-links when the token has ≥3 dot-separated segments
 * OR a path component. Excludes filenames (`report.txt`), email addresses
 * (preceded by `@`), and URLs that already have a scheme (preceded by `/`
 * or a word char). Trailing sentence punctuation is left outside the link.
 */
function autolinkBareDomains(text: string): string {
  if (!text) return text;
  // 2+ segments minimum (e.g. `geomet.ai`, `app.dealton.ai`); the in-handler
  // check below skips bare 2-segment hosts that have no path.
  const re = /(^|[^@\w./])([a-zA-Z][\w-]*(?:\.[a-zA-Z][\w-]*)*\.[a-zA-Z]{2,}(?:\/[^\s)\]"<>]*)?)/g;
  return text.replace(re, (match, prefix, token) => {
    const hasPath = token.includes('/');
    const host = hasPath ? token.split('/')[0] : token;
    const segments = host.split('.');
    if (segments.length < 3 && !hasPath) return match;       // bare 2-segment domain (risky)
    const trailMatch = token.match(/[.,;:!?)\]}]+$/);
    const trail = trailMatch ? trailMatch[0] : '';
    const url = trail ? token.slice(0, -trail.length) : token;
    return `${prefix}[${url}](https://${url})${trail}`;
  });
}

/**
 * Undo the ~72–78 column hard wrap mail clients apply to `text/plain` on send.
 *
 * An ingested plaintext body arrives with literal newlines at the SENDER's wrap
 * column (the RFC 2822 convention; Outlook and most desktop clients do it).
 * Those newlines describe the sender's line width, not ours — rendering them
 * verbatim (`breaks: true` below, or `white-space: pre-wrap`) breaks a 1140px
 * thread column at ~565px, which reads as broken text next to the HTML-sourced
 * messages in the same thread. Every real mail reader unwraps before display.
 *
 * Evidence-gated on purpose: the unwrap only runs when the body actually looks
 * hard-wrapped (≥2 prose lines landing in a 62–98 column band). Bodies that are
 * already soft-wrapped — HTML mail flattened to text, and anything George
 * drafted, where a single \n IS meaningful — pass through untouched. Blank-line
 * paragraph breaks always survive; only intra-paragraph wraps are joined.
 *
 * Known limits, both deliberate:
 *   - A sign-off following the last paragraph with NO blank line between them
 *     gets joined onto it. Guarded for the common openers (Regards / Thanks /
 *     Best / …); a bare name on its own line still merges.
 *   - One wrapped line isn't evidence of a wrap column, so a body with a single
 *     long line stays as-is — e.g. the "On <date>, <name> <addr>\nwrote:"
 *     attribution keeps its break. Lowering the gate to 1 would also start
 *     unwrapping bodies whose only long line is a bare URL, which is worse.
 *
 * Verified 2026-07-26 over the 69 ingested messages cached for
 * geomet.ai/Barrick_Gold: 30 unwrapped, and every HTML-sourced inbound (line
 * widths 100–398) plus every one-fact-per-line block (Webex dial-ins, signature
 * blocks) left untouched.
 */
// A line that OPENS its own block — never folded up into the line above it.
const OPENS_BLOCK =
  /^\s*(?:>|#{1,6}\s|[-*+•]\s|\d+[.)]\s|\||```|~~~|-{3,}|_{3,}|\*{3,}|--\s*$)/;
// A line that can never ABSORB the line below it. Narrower than OPENS_BLOCK on
// purpose: a wrapped list item ("1. …" / "- …") must still swallow its own
// continuation lines, or the item ends up half-unwrapped — which looks worse
// than leaving it alone.
const NO_ABSORB = /^\s*(?:>|#{1,6}\s|```|~~~|-{3,}|_{3,}|\*{3,}|--\s*$)/;
// Quoted-reply header blocks ("From: … / Sent: … / Subject: …") carry one fact
// per line — joining them produces a run-on.
const HEADER_LINE = /^\s*(?:from|to|cc|bcc|sent|date|subject|reply-to)\s*:/i;
const SIGNOFF =
  /^\s*(?:regards|kind regards|best|best regards|warm regards|thanks|thanks again|thank you|cheers|sincerely|yours|talk soon|speak soon)\b[\s,.!—-]*$/i;
// Indented → preformatted / table-ish. Left exactly as the sender typed it.
const LIST_ITEM = /^\s*(?:[-*\u2022]\s|\d+[.)]\s)/;
const PREFORMATTED = /^\s{2,}\S/;

function opensBlock(line: string): boolean {
  return (
    !line.trim() || OPENS_BLOCK.test(line) || HEADER_LINE.test(line) || PREFORMATTED.test(line)
  );
}
function canAbsorb(line: string): boolean {
  return (
    !!line.trim() && !NO_ABSORB.test(line) && !HEADER_LINE.test(line) && !PREFORMATTED.test(line)
  );
}

/* A cp1252 sender's apostrophe (0x92) was decoded as UTF-8 at ingest and stored as U+FFFD.
   The byte is gone, so this is a DISPLAY repair and deliberately a narrow one: only a
   replacement char sitting between a word character and a contraction/possessive suffix,
   where nothing else could have been there.

   ⚠️ It must NOT touch an accented letter that was corrupted the same way. Measured over
   the 447 U+FFFD in the ingested corpus: this repairs 175 (`GeoMet?s`, `I?m`, `I?ll`,
   `it?s`, `can?t`) and leaves `C?sar`, `Vizca?no`, `Andr?`, `Lef?vre`, `pr?f?rez` and every
   curly quote alone — those stay visibly broken, which is correct. §7: repair only what is
   unambiguous, never guess.

   The real fix is upstream (`email_common.decode_mime`) plus a Gmail re-ingest of the
   already-corrupted threads. */
const LOST_APOSTROPHE = /(\w)\ufffd(s|t|d|m|re|ve|ll)\b/g;

export function repairLostApostrophes(text: string): string {
  return text ? text.replace(LOST_APOSTROPHE, '$1\u2019$2') : text;
}

// Inline-image Content IDs are transport markers, not sender prose. Remove them
// only when displaying mail; the stored message body remains unchanged.
export function stripCids(text: string): string {
  return text.replace(/\[cid:[^\]]+\]/gi, '').replace(/[ \t]{2,}/g, ' ');
}

export function unwrapHardBreaks(text: string): string {
  if (!text) return text;
  const src = text.replace(/\r\n?/g, '\n');
  const lines = src.split('\n');
  const widths = lines
    .filter(canAbsorb)
    .map((l) => l.trimEnd().length)
    .filter((n) => n >= 62 && n <= 98);
  if (widths.length < 2) return src;   // no wrap column → nothing to undo
  // The widest wrapped line ≈ the sender's wrap column. A line within ~25 chars
  // of it could have been cut there (one long trailing word's worth of slack);
  // anything shorter than that was a break the sender typed.
  const floor = Math.max(40, Math.max(...widths) - 25);
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const prev = out.length ? out[out.length - 1] : '';
    // A hard wrap can drop a continuation onto a line STARTING with "- ", which markdown
    // then renders as a stray bullet: "…Director Geology" / "• US, based in Chicago".
    // Rescue only a LONE item — a genuine list has siblings, or a colon introducing it.
    // Every guard is load-bearing; drop any one and real lists collapse into prose.
    // The sibling search SKIPS BLANK LINES: a loose list ("- a\n\n- b") is still a list,
    // and stopping at the first blank swallowed its opening item.
    let next = '';
    for (let j = i + 1; j < lines.length; j++) {
      if (lines[j].trim()) { next = lines[j]; break; }
    }
    const loneListItem =
      LIST_ITEM.test(line) &&
      !LIST_ITEM.test(next) &&
      !LIST_ITEM.test(prev) &&
      !/:\s*$/.test(prev);
    const joinable =
      canAbsorb(prev) &&
      (!opensBlock(line) || loneListItem) &&
      !SIGNOFF.test(line) &&
      prev.trimEnd().length >= floor;
    if (joinable) out[out.length - 1] = prev.trimEnd() + ' ' + line.trim();
    else out.push(line);
  }
  return out.join('\n');
}

export function renderMarkdown(text: string): string {
  if (!text) return '';
  const preprocessed = autolinkBareDomains(text);
  return marked.parse(preprocessed, { async: false }) as string;
}

/** Render model/mail Markdown in a restricted HTML vocabulary.
 *
 * `marked` intentionally preserves raw HTML, so its output cannot go straight
 * into `{@html}` when the text may quote untrusted mailbox content. Parse in an
 * inert template, keep only formatting tags, and strip every attribute except
 * a safe link target. Use this for Sales and Fund content that may contain
 * mailbox, model, or uploaded text.
 */
export function renderSafeMarkdown(text: string): string {
  if (!text) return '';
  const escaped = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  if (typeof document === 'undefined') return `<p>${escaped(text).replace(/\n/g, '<br>')}</p>`;

  const allowed = new Set([
    'P', 'BR', 'H1', 'H2', 'H3', 'H4', 'STRONG', 'EM', 'UL', 'OL', 'LI',
    'BLOCKQUOTE', 'PRE', 'CODE', 'HR', 'DEL', 'A',
    'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD',
  ]);
  const template = document.createElement('template');
  template.innerHTML = renderMarkdown(text);
  const clean = (parent: Node) => {
    for (const child of Array.from(parent.childNodes)) {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.parentNode?.removeChild(child);
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      const element = child as HTMLElement;
      if (!allowed.has(element.tagName)) {
        element.replaceWith(document.createTextNode(element.textContent || ''));
        continue;
      }
      const href = element.tagName === 'A' ? element.getAttribute('href') : null;
      for (const attribute of Array.from(element.attributes)) element.removeAttribute(attribute.name);
      if (href) {
        try {
          const parsed = new URL(href, document.baseURI);
          if (['http:', 'https:', 'mailto:'].includes(parsed.protocol)) {
            element.setAttribute('href', href);
            element.setAttribute('rel', 'noopener noreferrer');
          }
        } catch { /* An invalid target stays plain text. */ }
      }
      clean(element);
    }
  };
  clean(template.content);
  return template.innerHTML;
}
